import Phaser from "phaser";
import type { Controls } from "./Controls";
import { KeyboardControls } from "./KeyboardControls";
import { GamepadControls } from "./GamepadControls";
import { TouchControls, isTouchDevice } from "./TouchControls";
import { AIControls, type AIDifficulty } from "./AIControls";
import { P1_KEYS, P2_KEYS } from "../config";

/**
 * Решает кто чем играет.
 *
 * Логика:
 *   - Если touch-устройство → P1 = touch, P2 = AI (одиночная игра с телефона)
 *   - Если оба геймпада подключены → P1 = pad0, P2 = pad1 (два игрока в клубе)
 *   - Если один геймпад → P1 = pad0, P2 = keyboard
 *   - Иначе → оба keyboard (домашний 2-player)
 *
 * Игрок может в любой момент нажать клавишу — мы переопределим источник.
 * (TODO: переопределение на лету; пока — статически на старте сцены.)
 */
export type InputProfile =
  | { kind: "keyboard-2p" }
  | { kind: "gamepad-2p" }
  | { kind: "gamepad-keyboard" }
  | { kind: "touch-vs-ai" };

export interface InputSetup {
  p1: Controls;
  p2: Controls;
  profile: InputProfile;
  /** Если P2 = AI, эту функцию нужно вызвать после создания обоих Fighter. */
  attachAI?: (p1Fighter: any, p2Fighter: any) => void;
}

export function detectAndCreate(scene: Phaser.Scene, aiDifficulty: AIDifficulty = "novice", forceAI = false): InputSetup {
  const gp = scene.input.gamepad;
  const padCount = gp ? gp.gamepads.filter(p => p && p.connected).length : 0;
  const isTouch = isTouchDevice() && (window.matchMedia("(pointer: coarse)").matches);

  // Touch — приоритет для мобильных
  if (isTouch && padCount === 0) {
    const p1 = new TouchControls(scene);
    const ai = new AIControls(aiDifficulty);
    return {
      p1, p2: ai,
      profile: { kind: "touch-vs-ai" },
      attachAI: (p1F, p2F) => ai.attach(p2F, p1F),
    };
  }

  // Тренировка: ВСЕГДА P2 = AI, независимо от устройства.
  if (forceAI) {
    const p1: Controls = padCount >= 1
      ? new GamepadControls(scene, 0)
      : new KeyboardControls(scene, P1_KEYS, "KEYBOARD P1");
    const ai = new AIControls(aiDifficulty);
    return {
      p1, p2: ai,
      profile: { kind: "touch-vs-ai" },
      attachAI: (p1F, p2F) => ai.attach(p2F, p1F),
    };
  }

  // 2 геймпада → два игрока
  if (padCount >= 2) {
    return {
      p1: new GamepadControls(scene, 0),
      p2: new GamepadControls(scene, 1),
      profile: { kind: "gamepad-2p" },
    };
  }

  // 1 геймпад → P1 геймпад, P2 клавиатура
  if (padCount === 1) {
    return {
      p1: new GamepadControls(scene, 0),
      p2: new KeyboardControls(scene, P2_KEYS, "KEYBOARD P2"),
      profile: { kind: "gamepad-keyboard" },
    };
  }

  // Default: оба на клавиатуре
  return {
    p1: new KeyboardControls(scene, P1_KEYS, "KEYBOARD P1"),
    p2: new KeyboardControls(scene, P2_KEYS, "KEYBOARD P2"),
    profile: { kind: "keyboard-2p" },
  };
}

/** Утилита: создать одиночные controls для меню/select навигации. */
export function createMenuControls(scene: Phaser.Scene, player: "p1" | "p2"): Controls {
  const gp = scene.input.gamepad;
  const padIdx = player === "p1" ? 0 : 1;
  const padOk = gp && gp.gamepads[padIdx]?.connected;
  if (padOk) return new GamepadControls(scene, padIdx);
  return new KeyboardControls(
    scene,
    player === "p1" ? P1_KEYS : P2_KEYS,
    `KEYBOARD ${player.toUpperCase()}`,
  );
}
